import { isStandablePlatform, type PlatformSolidityInput } from '../logic/PlatformSolidity';
import { TOWN_PROP_TEMPLATES, resolveTownPropTemplate, resolveTownPropAttachment } from './townPropTemplates';

export interface TownMountRect { readonly x: number; readonly y: number; readonly width: number; readonly height: number }
export interface TownMountProp {
  kind?: string; template?: unknown; x?: number; y?: number; width?: number; height?: number;
  flipX?: boolean; alpha?: number; tint?: string | number;
}
export interface TownMountPlatform extends PlatformSolidityInput { x?: number; y?: number; width?: number; height?: number }
export interface TownMountWorld { width: number; height: number; platforms?: readonly TownMountPlatform[] }
export interface TownMountError { index: number; message: string }
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

function positiveRect(rect: TownMountRect): boolean {
  return [rect.x, rect.y, rect.width, rect.height, rect.x + rect.width, rect.y + rect.height].every(finite)
    && rect.width > 0 && rect.height > 0;
}
function propBounds(prop: TownMountProp): TownMountRect | null {
  if (!prop || typeof prop !== 'object') return null;
  if (![prop.x, prop.y, prop.width, prop.height].every(finite)
    || (prop.flipX !== undefined && typeof prop.flipX !== 'boolean')) return null;
  const rect = { x: prop.x! - prop.width! / 2, y: prop.y! - prop.height!, width: prop.width!, height: prop.height! };
  return positiveRect(rect) ? rect : null;
}
function contains(outer: TownMountRect, inner: TownMountRect): boolean {
  return inner.x >= outer.x && inner.y >= outer.y && inner.x + inner.width <= outer.x + outer.width
    && inner.y + inner.height <= outer.y + outer.height;
}
function inWorld(rect: TownMountRect, world: TownMountWorld): boolean {
  return finite(world.width) && finite(world.height) && world.width > 0 && world.height > 0
    && contains({ x: 0, y: 0, width: world.width, height: world.height }, rect);
}

/** Transform the actual local painted region through the same bottom-center scale/flip as the sprite. */
export function townMountRectInWorld(prop: TownMountProp, frame: Readonly<{ w: number; h: number }>, local: TownMountRect): TownMountRect | null {
  const bounds = propBounds(prop);
  if (!bounds || !finite(frame.w) || !finite(frame.h) || frame.w <= 0 || frame.h <= 0
    || !positiveRect(local) || !contains({ x: 0, y: 0, width: frame.w, height: frame.h }, local)) return null;
  const x = prop.flipX ? frame.w - local.x - local.width : local.x;
  const result = { x: bounds.x + x * bounds.width / frame.w, y: bounds.y + local.y * bounds.height / frame.h,
    width: local.width * bounds.width / frame.w, height: local.height * bounds.height / frame.h };
  return positiveRect(result) ? result : null;
}

function visibleHost(prop: TownMountProp): boolean {
  if (!prop || typeof prop !== 'object') return false;
  const tint = prop.tint;
  return (finite(tint) || (typeof tint === 'string' && /^#[0-9a-f]{6}$/i.test(tint)))
    && (prop.alpha === undefined || (finite(prop.alpha) && prop.alpha > 0));
}

/** Frame-local point through the same bottom-center scale/flip as the sprite. */
export function townMountPointInWorld(prop: TownMountProp, frame: Readonly<{ w: number; h: number }>, local: { x: number; y: number }): { x: number; y: number } | null {
  const bounds = propBounds(prop);
  if (!bounds || !finite(frame.w) || !finite(frame.h) || frame.w <= 0 || frame.h <= 0
    || !finite(local.x) || !finite(local.y) || local.x < 0 || local.y < 0 || local.x > frame.w || local.y > frame.h) return null;
  const x = prop.flipX ? frame.w - local.x : local.x;
  return { x: bounds.x + x * bounds.width / frame.w, y: bounds.y + local.y * bounds.height / frame.h };
}

const HOOK_TOLERANCE_PX = 1;
const MAX_HOOK_CHAIN = 4;

/** Index of the visible host whose catalog hookPoint meets this prop's hangPoint, or -1. */
function hookHostIndex(props: readonly TownMountProp[], index: number): number {
  const prop = props[index];
  const id = resolveTownPropTemplate(prop?.kind ?? '', prop?.template);
  const meta = id ? TOWN_PROP_TEMPLATES[id] : undefined;
  if (!meta || !('hangPoint' in meta)) return -1;
  const hang = townMountPointInWorld(prop, meta.frame, meta.hangPoint as { x: number; y: number });
  if (!hang) return -1;
  return props.findIndex((host, hostIndex) => {
    if (hostIndex === index || !visibleHost(host)) return false;
    const hostId = resolveTownPropTemplate(host.kind ?? '', host.template);
    const hostMeta = hostId ? TOWN_PROP_TEMPLATES[hostId] : undefined;
    if (!hostMeta || !('hookPoints' in hostMeta)) return false;
    return (hostMeta.hookPoints as readonly { x: number; y: number }[]).some(point => {
      const hook = townMountPointInWorld(host, hostMeta.frame, point);
      return !!hook && Math.abs(hook.x - hang.x) <= HOOK_TOLERANCE_PX && Math.abs(hook.y - hang.y) <= HOOK_TOLERANCE_PX;
    });
  });
}

/** A hook chain must end on a non-hook host within a few links (no cycles, no floating chains). */
function hookChainGrounded(props: readonly TownMountProp[], index: number): boolean {
  let current = index;
  for (let step = 0; step < MAX_HOOK_CHAIN; step++) {
    const host = hookHostIndex(props, current);
    if (host < 0) return false;
    const hostAttachment = resolveTownPropAttachment(props[host].kind ?? '', props[host].template);
    if (hostAttachment !== 'hook') return true;
    current = host;
  }
  return false;
}

/** Only compatible catalog templates obtain wall/ceiling support. Ordinary props keep their own ground policy. */
export function validateTownPropAttachments(props: readonly TownMountProp[], world: TownMountWorld): TownMountError[] {
  const errors: TownMountError[] = [];
  props.forEach((prop, index) => {
    const attachment = resolveTownPropAttachment(prop?.kind ?? '', prop?.template);
    if (attachment === 'ground') return;
    const bounds = propBounds(prop);
    if (!bounds) {
      errors.push({ index, message: `nature prop ${prop?.kind ?? '<missing kind>'} has invalid authored geometry` }); return;
    }
    let supported = false;
    let requiresFrame = false;
    if (inWorld(bounds, world) && attachment === 'ceiling') {
      supported = (world.platforms ?? []).some(platform => {
        if (!isStandablePlatform(platform) || platform.type === 'decorative') return false;
        const { x, y, width, height } = platform;
        if (![x, y, width, height].every(finite)) return false;
        const roof = { x: x!, y: y!, width: width!, height: height! };
        return positiveRect(roof) && inWorld(roof, world) && bounds.y === roof.y + roof.height
          && bounds.x >= roof.x && bounds.x + bounds.width <= roof.x + roof.width;
      });
    } else if (inWorld(bounds, world) && attachment === 'wall') {
      const template = TOWN_PROP_TEMPLATES[resolveTownPropTemplate(prop.kind!, prop.template)!];
      const patch = 'mountPatch' in template ? townMountRectInWorld(prop, template.frame, template.mountPatch) : null;
      requiresFrame = 'wallCoverage' in template && template.wallCoverage === 'frame';
      supported = !!patch && props.some((host, hostIndex) => {
        if (hostIndex === index || !visibleHost(host)) return false;
        const hostId = resolveTownPropTemplate(host.kind ?? '', host.template);
        const metadata = hostId ? TOWN_PROP_TEMPLATES[hostId] : undefined;
        const hostBounds = propBounds(host);
        return !!hostBounds && inWorld(hostBounds, world) && !!metadata && 'wallRegions' in metadata
          && metadata.wallRegions.some(region => {
            const painted = townMountRectInWorld(host, metadata.frame, region);
            return !!painted && contains(painted, patch!) && (!requiresFrame || contains(painted, bounds));
          });
      });
    } else if (inWorld(bounds, world) && attachment === 'hook') {
      supported = hookChainGrounded(props, index);
    } else if (inWorld(bounds, world) && attachment === 'backdrop') {
      supported = true;
    } else if (inWorld(bounds, world) && attachment === 'surface') {
      supported = props.some((host, hostIndex) => {
        if (hostIndex === index || !visibleHost(host)) return false;
        const hostId = resolveTownPropTemplate(host.kind ?? '', host.template);
        const metadata = hostId ? TOWN_PROP_TEMPLATES[hostId] : undefined;
        const hostBounds = propBounds(host);
        if (!hostBounds || !inWorld(hostBounds, world) || !metadata || !('supportSurfaces' in metadata)) return false;
        const surfaces = (metadata as { supportSurfaces: readonly TownMountRect[] }).supportSurfaces;
        return surfaces.some(surface => {
          const painted = townMountRectInWorld(host, metadata.frame, surface);
          if (!painted) return false;
          const contactX = prop.x!;
          const contactY = prop.y!;
          const xSupported = contactX >= painted.x && contactX <= painted.x + painted.width;
          const ySupported = Math.abs(contactY - painted.y) <= 4 || (contactY >= painted.y && contactY <= painted.y + painted.height + 4);
          return xSupported && ySupported;
        });
      });
      if (!supported && world.platforms) {
        supported = world.platforms.some(platform => {
          if (!isStandablePlatform(platform) || platform.type === 'decorative') return false;
          const { x, y, width } = platform;
          if (![x, y, width].every(finite)) return false;
          const contactX = prop.x!;
          const contactY = prop.y!;
          return contactX >= x! && contactX <= x! + width! && Math.abs(contactY - y!) <= 4;
        });
      }
    }
    if (!supported) errors.push({ index, message: attachment === 'ceiling'
      ? `nature prop ${prop.kind} at ${prop.x},${prop.y} has a top-center attachment outside a valid platform underside or world bounds`
      : attachment === 'hook'
        ? `nature prop ${prop.kind} at ${prop.x},${prop.y} has a hook mount that does not meet a visible host hook point grounded within ${MAX_HOOK_CHAIN} links`
      : attachment === 'backdrop'
        ? `nature prop ${prop.kind} at ${prop.x},${prop.y} has a backdrop mount outside world bounds`
        : attachment === 'surface'
          ? `nature prop ${prop.kind} at ${prop.x},${prop.y} has a surface mount outside a compatible visible host support surface, platform or world bounds`
          : requiresFrame
            ? `nature prop ${prop.kind} at ${prop.x},${prop.y} has a wall mount whose complete frame is outside a compatible visible wall region or world bounds`
            : `nature prop ${prop.kind} at ${prop.x},${prop.y} has a wall mount outside a compatible visible wall region or world bounds` });
  });
  return errors;
}
